import { config, gameUrlOverride } from '@/config/env'
import { fetchReleaseBuild, ManifestError, resolveManifestUrl, type ReleaseBuild } from './manifest'
import type { Catalog, Game } from './types'

export type Availability = 'playable' | 'coming-soon' | 'unavailable' | 'no-build'

/**
 * Resolves a fixed build URL for a game, or null when it has none. Order:
 * per-game env override → catalog entry URL. Relative entry paths resolve
 * against VITE_GAMES_BASE_URL (falling back to this site).
 */
function resolveStaticBuildUrl(game: Game, includeEntry: boolean): string | null {
  const raw = gameUrlOverride(game.id) ?? (includeEntry ? game.entry.url : null)
  if (!raw) return null
  try {
    const base = config.gamesBaseUrl ? config.gamesBaseUrl.replace(/\/?$/, '/') : window.location.origin
    const url = new URL(raw, base)
    return url.protocol === 'https:' || url.protocol === 'http:' ? url.href : null
  } catch {
    return null
  }
}

/** Whether the game has a build to play (a fixed URL or a valid release manifest). */
function hasBuild(game: Game): boolean {
  return resolveStaticBuildUrl(game, true) !== null || (game.entry.manifest !== null && resolveManifestUrl(game.entry.manifest) !== null)
}

/**
 * Determines the build to load for a new play session. A developer override
 * wins; then the release manifest, fetched fresh each time; then the entry URL.
 */
export async function resolveBuild(game: Game, signal: AbortSignal): Promise<ReleaseBuild> {
  const override = resolveStaticBuildUrl(game, false)
  if (override) return { url: override, version: null }

  const manifestUrl = game.entry.manifest ? resolveManifestUrl(game.entry.manifest) : null
  if (manifestUrl) return fetchReleaseBuild(game, manifestUrl, signal)

  const url = resolveStaticBuildUrl(game, true)
  if (!url) throw new ManifestError('This game has no playable build.')
  return { url, version: game.version }
}

export function getAvailability(game: Game): Availability {
  if (game.status === 'unavailable') return 'unavailable'
  if (hasBuild(game)) return 'playable'
  return game.status === 'coming-soon' ? 'coming-soon' : 'no-build'
}

export const availabilityLabel: Record<Availability, string> = {
  playable: 'Playable',
  'coming-soon': 'Coming soon',
  unavailable: 'Unavailable',
  'no-build': 'No build yet',
}

export function findGame(catalog: Catalog, id: string | undefined): Game | undefined {
  return catalog.games.find((game) => game.id === id)
}

/** "puzzle" → "Puzzle", "tower-defense" → "Tower Defense" */
export function genreLabel(genre: string): string {
  return genre.replace(/-/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase())
}

export interface GenreSummary {
  id: string
  label: string
  count: number
}

export function getGenres(games: Game[]): GenreSummary[] {
  const counts = new Map<string, number>()
  for (const game of games) for (const genre of game.genres) counts.set(genre, (counts.get(genre) ?? 0) + 1)
  return [...counts]
    .map(([id, count]) => ({ id, label: genreLabel(id), count }))
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label))
}

export const SORT_OPTIONS = [
  { id: 'newest', label: 'Recently added' },
  { id: 'title', label: 'Title A–Z' },
  { id: 'release', label: 'Release date' },
] as const
export type SortOption = (typeof SORT_OPTIONS)[number]['id']

export function isSortOption(value: string | null): value is SortOption {
  return SORT_OPTIONS.some((option) => option.id === value)
}

const byTitle = (a: Game, b: Game) => a.title.localeCompare(b.title)

const comparators: Record<SortOption, (a: Game, b: Game) => number> = {
  newest: (a, b) => b.addedAt.localeCompare(a.addedAt) || byTitle(a, b),
  title: byTitle,
  // Released games first (newest release first), unreleased games last.
  release: (a, b) => (b.releaseDate ?? '').localeCompare(a.releaseDate ?? '') || byTitle(a, b),
}

export function sortGames(games: Game[], sort: SortOption): Game[] {
  return [...games].sort(comparators[sort])
}

function normalize(text: string): string {
  return text.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase().trim()
}

export interface GameQuery {
  search: string
  genre: string | null
  sort: SortOption
}

export function queryGames(games: Game[], { search, genre, sort }: GameQuery): Game[] {
  const needle = normalize(search)
  const filtered = games.filter(
    (game) => (!genre || game.genres.includes(genre)) && (!needle || normalize(game.title).includes(needle)),
  )
  return sortGames(filtered, sort)
}
